/*
Copyright 2022 New Vector Ltd

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/

import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import { GroupCall, GroupCallEvent } from "matrix-js-sdk/src/webrtc/groupCall";
import { CallEvent, MatrixCall } from "matrix-js-sdk/src/webrtc/call";

export function useSentryGroupCallHandler(groupCall: GroupCall) {
  useEffect(() => {
    function onHangup(call: MatrixCall) {
      if (call.hangupReason === "ice_failed") {
        Sentry.captureException(new Error("Call hangup due to ICE failure."));
      }
    }

    function onError(error: Error) {
      Sentry.captureException(error);
    }

    if (groupCall) {
      groupCall.on(CallEvent.Hangup, onHangup);
      groupCall.on(GroupCallEvent.Error, onError);
    }

    return () => {
      if (groupCall) {
        groupCall.removeListener(CallEvent.Hangup, onHangup);
        groupCall.removeListener(GroupCallEvent.Error, onError);
      }
    };
  }, [groupCall]);
}
